import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton
} from '@mui/material';
import {
  PrivacyTip as PrivacyIcon,
  HealthAndSafety as HipaaIcon,
  Security as SecurityIcon,
  OpenInNew as OpenInNewIcon,
  ArrowForward as ArrowForwardIcon,
  Sync as SyncIcon,
  ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { get, authPost } from '../../services/apiService';
import { format } from 'date-fns';
import { isAuthenticated } from '../../services/authService';

const darkGreenColor = "#1D4434";

/** Donut showing completion % with number in center; color by completion (red / amber / green). Same as Planning tab. */
function CompletionDonut({ percentage = 0 }) {
  const value = Math.min(Math.max(Number(percentage), 0), 100);
  const color = value < 33 ? 'error' : value < 67 ? 'warning' : 'success';
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress
        variant="determinate"
        value={value}
        color={color}
        size={40}
        thickness={5}
      />
      <Box sx={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" fontWeight="medium" color="text.primary">
          {Math.round(value)}%
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * HIPAA/CUI Product Engineering - Shows controls progress cards and feature table
 * @returns {JSX.Element} HipaaCuiProductEngineering component
 */
const HipaaCuiProductEngineering = ({ initialFilter }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [features, setFeatures] = useState([]);
  const [filteredFeatures, setFilteredFeatures] = useState([]);
  const [filters, setFilters] = useState({
    controlType: initialFilter || '',
    team: '',
    fixVersion: ''
  });
  const [expandedFeatures, setExpandedFeatures] = useState(new Set());
  const [featureChildren, setFeatureChildren] = useState({});
  const [loadingChildren, setLoadingChildren] = useState(new Set());

  useEffect(() => {
    // Update filter when initialFilter prop changes
    if (initialFilter) {
      setFilters(prev => ({ ...prev, controlType: initialFilter }));
    }
  }, [initialFilter]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [features, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [dashboardResponse, featuresResponse] = await Promise.all([
        get('/api/hipaa-cui/dashboard'),
        get('/api/hipaa-cui/features')
      ]);
      
      if (dashboardResponse.success) {
        setDashboardData(dashboardResponse.data);
      }
      
      if (featuresResponse.success) {
        setFeatures(featuresResponse.data || []);
      }
    } catch (err) {
      console.error('Error loading HIPAA/CUI data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleForceSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSyncSuccess(null);
      
      const response = await authPost('/api/hipaa-cui/sync');
      
      if (response.success) {
        setSyncSuccess('Progress refreshed from Work Items.');
        setTimeout(() => setSyncSuccess(null), 5000);
        await loadData();
      } else {
        setError(response.error || response.message || 'Failed to sync HIPAA/CUI data');
      }
    } catch (err) {
      console.error('Error syncing HIPAA/CUI data:', err);
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to sync HIPAA/CUI data');
    } finally {
      setSyncing(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...features];
    
    if (filters.controlType) {
      filtered = filtered.filter(f => f.controlType === filters.controlType);
    }
    if (filters.team) {
      filtered = filtered.filter(f => f.team === filters.team);
    }
    if (filters.fixVersion) {
      filtered = filtered.filter(f => f.fixVersion === filters.fixVersion);
    }
    
    setFilteredFeatures(filtered);
  };

  const toggleFeatureExpand = useCallback(async (featureId) => {
    const id = String(featureId);
    setExpandedFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (!featureChildren[id]) {
      setLoadingChildren((prev) => new Set(prev).add(id));
      try {
        const res = await get(`/api/hipaa-cui/features/${id}/children`);
        setFeatureChildren((prev) => ({ ...prev, [id]: res.data || [] }));
      } catch (err) {
        console.error('Error loading feature children:', err);
        setFeatureChildren((prev) => ({ ...prev, [id]: [] }));
      } finally {
        setLoadingChildren((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }
  }, [featureChildren]);


  const renderControlCard = (title, icon, data, color, controlType) => {
    const completionRate = data?.avgCompletionRate || 0;
    const totalFeatures = data?.totalFeatures || 0;
    const completedStoryPoints = data?.completedStoryPoints || 0;
    const totalStoryPoints = data?.totalStoryPoints || 0;
    const isSelected = filters.controlType === controlType;

    const handleCardClick = () => {
      // Toggle filter: if already selected, clear filter; otherwise set it
      if (isSelected) {
        setFilters({ ...filters, controlType: '' });
      } else {
        setFilters({ ...filters, controlType: controlType });
      }
    };

    return (
      <Card 
        sx={{ 
          height: '100%',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'visible',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: isSelected ? `2px solid ${color}` : '1px solid',
          borderColor: isSelected ? color : 'divider',
          backgroundColor: isSelected ? `${color}08` : 'background.paper',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: `0 8px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px ${color}40`,
            '& .card-icon': {
              transform: 'scale(1.1)',
            },
            '& .arrow-icon': {
              opacity: 1,
              transform: 'translateX(4px)',
            }
          }
        }}
        elevation={isSelected ? 4 : 2}
        onClick={handleCardClick}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" flex={1}>
              <Box
                className="card-icon"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  bgcolor: `${color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                  transition: 'all 0.3s ease'
                }}
              >
                {React.createElement(icon, { sx: { color: color, fontSize: 24 } })}
              </Box>
              <Typography variant="h6" fontWeight={600}>
                {title}
              </Typography>
            </Box>
            <ArrowForwardIcon 
              className="arrow-icon"
              sx={{ 
                color: color, 
                opacity: isSelected ? 1 : 0.4,
                transition: 'all 0.3s ease',
                fontSize: 18
              }} 
            />
          </Box>
          
          <Box mb={2}>
            <Typography 
              variant="h3" 
              fontWeight={700}
              sx={{ 
                color: color,
                mb: 1,
                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {completionRate}%
            </Typography>
          </Box>

          <Box 
            sx={{ 
              display: 'flex', 
              gap: 1.5,
              pt: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box flex={1}>
              <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                Features
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {totalFeatures}
              </Typography>
            </Box>
            <Box flex={1}>
              <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                Story Points
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {completedStoryPoints} / {totalStoryPoints}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Get unique values for filters
  const uniqueTeams = [...new Set(features.map(f => f.team).filter(Boolean))].sort();
  const uniqueFixVersions = [...new Set(features.map(f => f.fixVersion).filter(Boolean))].sort();

  // Generate ADO URL for a feature
  const getAdoUrl = (featureId) => {
    if (!featureId) return '#';
    const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
    const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
    const baseUrl = adoOrg.includes('visualstudio.com')
      ? `https://${adoOrg}`
      : `https://dev.azure.com/${adoOrg}`;
    return `${baseUrl}/${encodeURIComponent(projectName)}/_workitems/edit/${featureId}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  // Format date for display
  const formatLastUpdated = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Compact Header */}
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Product Engineering Progress
        </Typography>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          {isAuthenticated() && (
            <Button
              variant="outlined"
              size="small"
              startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleForceSync}
              disabled={syncing || loading}
              sx={{
                borderColor: darkGreenColor,
                color: darkGreenColor,
                '&:hover': {
                  borderColor: darkGreenColor,
                  backgroundColor: `${darkGreenColor}08`
                }
              }}
            >
              {syncing ? 'Refreshing...' : 'Refresh from Work Items'}
            </Button>
          )}
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            {dashboardData?.lastSync && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                Last refresh: {formatLastUpdated(dashboardData.lastSync)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {syncSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncSuccess(null)}>
          {syncSuccess}
        </Alert>
      )}

      {/* Control Cards - Compact */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} lg={4}>
          {renderControlCard(
            'Privacy Control',
            PrivacyIcon,
            dashboardData?.privacyControl,
            '#1976d2',
            'Privacy Control'
          )}
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          {renderControlCard(
            'HIPAA Control',
            HipaaIcon,
            dashboardData?.hipaaControl,
            '#d32f2f',
            'HIPAA Control'
          )}
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          {renderControlCard(
            'Security Control',
            SecurityIcon,
            dashboardData?.securityControl,
            '#ed6c02',
            'Security Control'
          )}
        </Grid>
      </Grid>

      {/* Compact Filters */}
      <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 1 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Control Type</InputLabel>
              <Select
                value={filters.controlType}
                onChange={(e) => setFilters({ ...filters, controlType: e.target.value })}
                label="Control Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Privacy Control">Privacy Control</MenuItem>
                <MenuItem value="HIPAA Control">HIPAA Control</MenuItem>
                <MenuItem value="Security Control">Security Control</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Team</InputLabel>
              <Select
                value={filters.team}
                onChange={(e) => setFilters({ ...filters, team: e.target.value })}
                label="Team"
              >
                <MenuItem value="">All</MenuItem>
                {uniqueTeams.map(team => (
                  <MenuItem key={team} value={team}>{team}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Fix Version</InputLabel>
              <Select
                value={filters.fixVersion}
                onChange={(e) => setFilters({ ...filters, fixVersion: e.target.value })}
                label="Fix Version"
              >
                <MenuItem value="">All</MenuItem>
                {uniqueFixVersions.map(version => (
                  <MenuItem key={version} value={version}>{version}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Features Table - Full Height */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          '& .MuiTable-root': {
            minWidth: 650
          }
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48, paddingRight: 0 }} />
              <TableCell>ADO ID</TableCell>
              <TableCell>Feature Title</TableCell>
              <TableCell>Control Type</TableCell>
              <TableCell>Team</TableCell>
              <TableCell align="right">Story Points</TableCell>
              <TableCell align="right">Completion</TableCell>
              <TableCell>Target Date</TableCell>
              <TableCell>Fix Version</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFeatures.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No features found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredFeatures.map((feature) => {
                const featureIdStr = String(feature.featureId || '');
                const isExpanded = expandedFeatures.has(featureIdStr);
                const children = featureChildren[featureIdStr];
                const isLoadingChildren = loadingChildren.has(featureIdStr);
                return (
                  <React.Fragment key={`${feature.epicId}-${feature.featureId}`}>
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: 'inherit'
                      }}
                      onClick={() => toggleFeatureExpand(feature.featureId)}
                    >
                      <TableCell sx={{ width: 48, paddingRight: 0, verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); toggleFeatureExpand(feature.featureId); }}
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isLoadingChildren ? (
                            <CircularProgress size={20} />
                          ) : isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ChevronRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {feature.featureId ? (
                          <Box
                            component="a"
                            href={getAdoUrl(feature.featureId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'primary.main',
                              textDecoration: 'none',
                              fontWeight: 500,
                              '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.dark'
                              }
                            }}
                          >
                            {feature.featureId}
                            <OpenInNewIcon fontSize="small" sx={{ fontSize: '0.875rem' }} />
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{feature.featureTitle}</TableCell>
                      <TableCell>
                        <Chip 
                          label={feature.controlType} 
                          size="small"
                          color={
                            feature.controlType === 'Privacy Control' ? 'primary' :
                            feature.controlType === 'HIPAA Control' ? 'error' : 'warning'
                          }
                        />
                      </TableCell>
                      <TableCell>{feature.team || '-'}</TableCell>
                      <TableCell align="right">
                        {feature.completedStoryPoints} / {feature.totalStoryPoints}
                      </TableCell>
                      <TableCell align="right">
                        <CompletionDonut percentage={feature.completionRate} />
                      </TableCell>
                      <TableCell>
                        {feature.targetDate ? format(new Date(feature.targetDate), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                      <TableCell>{feature.fixVersion || '-'}</TableCell>
                    </TableRow>
                    {isExpanded && children && children.length > 0 && children.map((child) => (
                      <TableRow
                        key={child.id}
                        hover
                        sx={{
                          backgroundColor: 'grey.50',
                          '&:hover': { backgroundColor: 'grey.100' }
                        }}
                      >
                        <TableCell sx={{ width: 48, paddingRight: 0, paddingLeft: 2 }} />
                        <TableCell>
                          <Box
                            component="a"
                            href={getAdoUrl(child.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'primary.main',
                              textDecoration: 'none',
                              fontWeight: 500,
                              pl: 3,
                              '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.dark'
                              }
                            }}
                          >
                            {child.id}
                            <OpenInNewIcon fontSize="small" sx={{ fontSize: '0.875rem' }} />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ pl: 3 }}>
                          <Typography variant="body2">{child.title}</Typography>
                          {child.workItemType && (
                            <Chip label={child.workItemType} size="small" variant="outlined" sx={{ ml: 1, height: 20 }} />
                          )}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>{feature.team || '-'}</TableCell>
                        <TableCell align="right">
                          {child.completedStoryPoints} / {child.totalStoryPoints}
                        </TableCell>
                        <TableCell align="right">
                          <CompletionDonut percentage={child.completionRate} />
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>{feature.fixVersion || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default HipaaCuiProductEngineering;

